<i18n>
{
  "en": {
    "printMenu": "Print Menu",
    "selectService": "Select a service",
    "selectZone": "Select a zone",
    "cacheLoading": "Loading menu data, please wait",
    "printModalSubtitle": "Select a day, a service, zone and language to print the menu.",
    "dataPickerRequired": "Select a date",
    "selectLangugage": "Select a language",
    "selectedSections": "Select the sections to be printed",
    "printMenuconfiguration": "Configuration",
    "showProductFeature": "Show allergens",
    "showProductDescription": "Show product description",
    "showProductPrice": "Show prices",
    "showSectionFreeText": "Show section free text",
    "showInTwoColumns": "Show in two columns format",
    "fontSizeFactor": "Font size factor",
    "small": "Small",
    "medium": "Medium",
    "large": "Large",
    "lineProductHeight": "Product height",
    "clausesHeight": "Clauses height",
    "sectionsHeight" : "Section height",
    "menuPrintConfigError": "Error saving menu print configuration",
    "sectionsHeightTitle": "Title section height",
    "featuresSizeLabel": "Allergens size",
    "showBorders": "Show borders",
    "repeatLogoPerPage": "Display logo on all pages",
    "showInformationText": "Display information text on all pages"
  },
  "es": {
    "printMenu": "Imprimir Menú",
    "selectService": "Selecciona un servicio",
    "selectZone": "Selecciona una zona",
    "cacheLoading": "Cargando datos de menú, por favor espere",
    "printModalSubtitle": "Selecciona un día, un servicio, zona y el lenguaje para poder imprimir el menú",
    "dataPickerRequired": "Selecciona una fecha",
    "selectLangugage": "Selecciona un lenguaje",
    "selectedSections": "Selecciones los apartados a imprimir",
    "printMenuconfiguration": "Configuración",
    "showProductFeature": "Mostrar alérgenos",
    "showProductDescription":  "Mostrar la descripción de los productos",
    "showProductPrice": "Mostrar el precio",
    "showSectionFreeText": "Mostrar el texto libre de las secciones",
    "showInTwoColumns": "Mostrar en formato de dos columnas",
    "fontSizeFactor": "Factor de tamaño de fuente",
    "small": "Pequeño",
    "medium": "Mediano",
    "large": "Grande",
    "lineProductHeight": "Altura del producto",
    "clausesHeight": "Altura del apartado",
    "sectionsHeight" : "Altura de la sección",
    "menuPrintConfigError":  "Error guardando la configuración del menú impreso",
    "sectionsHeightTitle":  "Tamaño del título de la sección",
    "featuresSizeLabel": "Tamaño de los alérgenos",
    "showBorders":  "Mostrar los bordes",
    "repeatLogoPerPage":  "Mostrar el logo en todas las páginas",
    "showInformationText": "Mostrar el texto de información en todas las páginas"
  }
}
</i18n>

<template>
  <div>
    <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
      <div v-if="showSpinner">
        <dynameat-spinner/>
        <p class="text-center">{{ $t('cacheLoading') }}</p>
      </div>
      <v-card v-else height="100%" class="px-7 py-5">
        <div class="d-flex align-center justify-space-between">
          <v-card-title class="word-break pa-0">
            {{ $t('printMenu') }}
          </v-card-title>
        </div>
        <v-card-subtitle class="pa-0 mb-3">{{ $t('printModalSubtitle') }}</v-card-subtitle>
        <v-container class="pa-0">
          <v-form class="d-flex flex-column align-left" ref="form">
            <v-card-subtitle class="pa-0 ma-0 mt-1 mb-1 text-center">
              {{ $t('dataPickerRequired') + ' *' }}
            </v-card-subtitle>
            <date-picker class="inline-block h-full mx-auto" v-model="date"
                         trim-weeks :locale="userLocale" ref="datepicker"/>
            <v-select v-model="selectedService" :items="serviceList"
                      :menu-props="{ maxHeight: '400' }"
                      :label="$t('selectService')" item-value="guid" item-text="name" return-object
                      class="required"/>
            <v-select v-model="selectedZone" :items="menuZones" :menu-props="{ maxHeight: '400' }"
                      :label="$t('selectZone')" :disabled="!isMenuSelected" item-value="guid"
                      item-text="name" return-object class="required"/>
            <v-select v-model="selectedLanguage" :items="menuLanguages"
                      :menu-props="{ maxHeight: '400' }"
                      :label="$t('selectLangugage')" item-value="guid"
                      item-text="name" return-object class="required"/>
            <v-select v-model="selectedSections" :items="printMenuSection"
                      :menu-props="{ maxHeight: '400' }"
                      :label="$t('selectedSections')" item-value="guid" multiple chips
                      item-text="title_alt1" class="required"/>
            <v-expansion-panels accordion class="mb-2" :disabled="!menuPrintConfiguration" v-model="menuPrintConfigurationPanel">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  {{ $t('printMenuconfiguration') }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-switch v-model="showProductFeature" :label="$t('showProductFeature')"/>
                  <v-switch v-model="showProductDescription" :label="$t('showProductDescription')"/>
                  <v-switch v-model="showProductPrice" :label="$t('showProductPrice')"/>
                  <v-switch v-model="showSectionFreeText" :label="$t('showSectionFreeText')"/>
                  <v-switch v-model="showInTwoColumns" :label="$t('showInTwoColumns')"/>
                  <v-switch v-model="showBorders" :label="$t('showBorders')"/>
                  <v-switch v-model="repeatLogoPerPage" :label="$t('repeatLogoPerPage')"/>
                  <v-switch v-model="showInformationText" :label="$t('showInformationText')"/>
                  <v-subheader class="pa-0" :style="primaryStyle">
                    {{ this.$t('fontSizeFactor') }}
                  </v-subheader>
                  <v-slider v-model="fontSizeFactor" color="#032c58" min="-5" max="5"
                            thumb-label/>
                  <v-select v-model="lineProductHeight" :items="heightSelection"
                            :menu-props="{ maxHeight: '400' }" :label="$t('lineProductHeight')"
                            item-value="value" item-text="name"/>
                  <v-select v-model="clausesHeight" :items="heightSelection"
                            :menu-props="{ maxHeight: '400' }" :label="$t('clausesHeight')"
                            item-value="value" item-text="name"/>
                  <v-select v-model="sectionsHeight" :items="heightSelection"
                            :menu-props="{ maxHeight: '400' }" :label="$t('sectionsHeight')"
                            item-value="value" item-text="name"/>
                  <v-select v-model="sectionsHeightTitle" :items="heightSelection"
                            :menu-props="{ maxHeight: '400' }" :label="$t('sectionsHeightTitle')"
                            item-value="value" item-text="name"/>
                  <v-select v-model="featuresSize" :items="heightSelection"
                            :menu-props="{ maxHeight: '400' }" :label="$t('featuresSizeLabel')"
                            item-value="value" item-text="name"/>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <dynameat-modal-buttons @cancel="onCancel" @confirm="onConfirm"
                                    :confirmDisabled="!formValid"
                                    :confirmText="$t('confirm')" class="mt-2"/>
          </v-form>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import DynameatSpinner from '@dynameat/components/DynameatSpinner';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import { MenuPrintConfiguration } from '@menu/models/menuPrintConfiguration';
import { menuPrintConfigurationEdit } from '../requests';

@Component
@Components({ DatePicker, DynameatSpinner, DynameatModalButtons })
@MapGetters(['saleZoneList', 'serviceList', 'isLoading', 'saleRateList', 'userLocale', 'pointOfSaleLanguages', 'printMenuSection', 'menuPrintConfiguration'])
@MapActions(['addNotification', 'setLoading', 'printMenuSectionRequest', 'menuPrintConfigurationRequest'])
export default class PrintMenuModal extends Vue {
  @Prop({ type: Boolean, default: false }) visible;

  @Prop({ default: null }) selectedMenu;

  valid = false;

  date = null

  selectedZone = null;

  selectedService = null;

  selectedLanguage = null;

  selectedSections = [];

  showProductFeature = false;

  showProductDescription = false;

  showProductPrice = false;

  showSectionFreeText = false;

  showInTwoColumns = false;

  showBorders = false;

  repeatLogoPerPage = false;

  showInformationText = false;

  fontSizeFactor = '';

  lineProductHeight = '';

  clausesHeight = '';

  sectionsHeight = '';

  sectionsHeightTitle = ''

  featuresSize = ''

  menuPrintConfigurationPanel = false;

  get showSpinner() {
    return this.visible && this.isLoading;
  }

  get isMenuSelected() {
    return !!this.selectedMenu;
  }

  get isDaySelected() {
    return !!this.date;
  }

  get isZoneSelected() {
    return !!this.selectedZone;
  }

  get isServiceSelected() {
    return !!this.selectedService;
  }

  get isLanguageSelected() {
    return !!this.selectedLanguage;
  }

  get isSelectedSections() {
    return this.selectedSections.length > 0;
  }

  get menuZones() {
    const saleRateNameArray = [];
    const localZoneList = this.isMenuSelected ? this.saleZoneList.filter((zone) => this.selectedMenu.sale_zones?.includes(zone.guid)) : this.saleZoneList;
    localZoneList.forEach((zone) => {
      const saleRate = this.saleRateList.find((rate) => rate.sale_zones?.find((el) => el.guid === zone.guid));
      const rateName = saleRate?.saleZoneNames;
      if (!saleRateNameArray.find((el) => el.name === rateName)) {
        saleRateNameArray.push({ name: zone.name, guid: zone.guid });
      }
    });
    return saleRateNameArray;
  }

  get formValid() {
    return this.isMenuSelected && this.isDaySelected && this.isZoneSelected && this.isServiceSelected && this.isLanguageSelected && this.isSelectedSections;
  }

  get menuLanguages() {
    if (this.selectedMenu) {
      return this.pointOfSaleLanguages.filter((each) => each.guid === this.selectedMenu.mainLanguage
        || each.guid === this.selectedMenu.secondLanguage
        || each.guid === this.selectedMenu.thirdLanguage);
    }
    return [];
  }

  get primaryStyle() {
    return {
      fontSize: `${12 + this.fontSizeFactor}px`
    };
  }

  get heightSelection() {
    return [
      { name: this.$t('small'), value: 'small' },
      { name: this.$t('medium'), value: 'medium' },
      { name: this.$t('large'), value: 'large' },
    ];
  }

  async onConfirm() {
    try {
      const actualPrintMenuConfiguration = MenuPrintConfiguration.toInstance(this.menuPrintConfiguration.serialize());
      actualPrintMenuConfiguration.showProductFeature = this.showProductFeature;
      actualPrintMenuConfiguration.showProductDescription = this.showProductDescription;
      actualPrintMenuConfiguration.showProductPrice = this.showProductPrice;
      actualPrintMenuConfiguration.showSectionFreeText = this.showSectionFreeText;
      actualPrintMenuConfiguration.showInTwoColumns = this.showInTwoColumns;
      actualPrintMenuConfiguration.showBorders = this.showBorders;
      actualPrintMenuConfiguration.fontSize = this.fontSizeFactor;
      actualPrintMenuConfiguration.lineProductHeight = this.lineProductHeight;
      actualPrintMenuConfiguration.clausesHeight = this.clausesHeight;
      actualPrintMenuConfiguration.sectionsHeight = this.sectionsHeight;
      actualPrintMenuConfiguration.sectionsHeightTitle = this.sectionsHeightTitle;
      actualPrintMenuConfiguration.featuresSize = this.featuresSize;
      actualPrintMenuConfiguration.repeatLogoPerPage = this.repeatLogoPerPage;
      actualPrintMenuConfiguration.showInformationText = this.showInformationText;
      menuPrintConfigurationEdit(actualPrintMenuConfiguration).then(() => {
        if (this.formValid) {
          this.$emit('confirm', {
            printMenuFormData: {
              date: this.date,
              selectedService: this.selectedService.guid,
              selectedZone: this.selectedZone.guid,
              selectedLanguage: this.selectedLanguage.guid,
              selectedMenu: this.selectedMenu.guid,
              selectedSections: this.selectedSections,
            }
          });
        }
      });
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('menuPrintConfigError'), null));
    }
  }

  onCancel() {
    this.clearOwnData();
    this.$emit('cancel');
  }

  clearOwnData() {
    this.date = null;
    this.selectedService = null;
    this.selectedZone = null;
    this.selectedLanguage = null;
    this.menuPrintConfigurationPanel = false;
  }

  @Watch('selectedMenu', { immediate: true })
  async onSelectedMenu(newMenu) {
    if (newMenu) {
      await this.printMenuSectionRequest({ menuGuid: newMenu.guid, type: 1 });
      await this.menuPrintConfigurationRequest(newMenu.menu_print_configuration);
    }
  }

  @Watch('printMenuSection', { immediate: true })
  onPrintMenuSection(newPrintMenuSections) {
    this.selectedSections = newPrintMenuSections.map((each) => each.guid);
  }

  @Watch('menuPrintConfiguration')
  onMenuPrintConfigurationChange(updatedMenuPrintConfiguration) {
    if (updatedMenuPrintConfiguration) {
      this.showProductFeature = updatedMenuPrintConfiguration.showProductFeature;
      this.showProductDescription = updatedMenuPrintConfiguration.showProductDescription;
      this.showProductPrice = updatedMenuPrintConfiguration.showProductPrice;
      this.showSectionFreeText = updatedMenuPrintConfiguration.showSectionFreeText;
      this.showInTwoColumns = updatedMenuPrintConfiguration.showInTwoColumns;
      this.showBorders = updatedMenuPrintConfiguration.showBorders;
      this.fontSizeFactor = updatedMenuPrintConfiguration.fontSize;
      this.lineProductHeight = updatedMenuPrintConfiguration.lineProductHeight;
      this.clausesHeight = updatedMenuPrintConfiguration.clausesHeight;
      this.sectionsHeight = updatedMenuPrintConfiguration.sectionsHeight;
      this.sectionsHeightTitle = updatedMenuPrintConfiguration.sectionsHeightTitle;
      this.featuresSize = updatedMenuPrintConfiguration.featuresSize;
      this.repeatLogoPerPage = updatedMenuPrintConfiguration.repeatLogoPerPage;
      this.showInformationText = updatedMenuPrintConfiguration.showInformationText;
    }
  }
}
</script>
<style scoped>
.progress-enter-active, .progress-leave-active {
  transition: opacity .5s
}

.progress-enter, .progress-leave-to /* .fade-leave-active below version 2.1.8 */
{
  opacity: 0
}

.word-break {
  word-break: break-word;
}

.required label::after {
  content: " *";
  color: red;
}
</style>
